import {
  userErrorMessage,
  foxKifuObservationTimelineResponseSchema,
  foxKifuAiDefaultResponseSchema,
  foxKifuAiAnalysisResponseSchema,
  foxKifuAiStatusResponseSchema,
  foxKifuAiVariationResponseSchema,
  foxKifuResearchResponseSchema,
  foxKifuObservationResearchResponseSchema,
  foxKifuObservationAiAnalysisResponseSchema,
  gameReviewResponseSchema,
  type BoardPoint,
  type FoxKifuAiAnalysis,
  type FoxKifuAiPublicProfile,
  type FoxKifuAiVariationData,
  type FoxKifuResearchData,
  type GameReviewResponse,
} from '@wx-weiqi/contracts';

import { localizeGameError } from '../domain/game-error';
import { nextRequestId, WebApiError } from '../services/http';
import {
  AI_ANALYSIS_MAX_BOUNDED_RETRIES,
  AI_ANALYSIS_NO_RESULT_STALE_MS,
  AI_ANALYSIS_POLL_INTERVAL_MS,
  AI_ANALYSIS_RUNNING_STALE_MS,
  AI_ANALYSIS_SLICE_RESTART_MS,
  aiRetryDelay,
  isRetryableAiError,
  waitForAi,
} from './ai-analysis-recovery';
import type { IdentityStore } from './identity-store';
import { selectionFromStatus, type AiAccountSelectionState } from './ai-account-selection';

export interface ObservationAnalysisPosition {
  readonly gameId: string;
  readonly stateVersion: number;
  readonly baseMoveNumber: number;
  readonly moves: readonly BoardPoint[];
}

export function observationPositionKey(position: ObservationAnalysisPosition): string {
  return JSON.stringify([
    position.gameId,
    position.stateVersion,
    position.baseMoveNumber,
    position.moves,
  ]);
}

export interface GameReplayState extends AiAccountSelectionState {
  readonly review: GameReviewResponse['data'] | null;
  readonly reviewStateVersion: number | null;
  readonly research: FoxKifuResearchData | null;
  readonly researchPending: boolean;
  readonly aiServiceEnabled: boolean | null;
  readonly aiAvailable: boolean | null;
  readonly aiConfigured: boolean | null;
  readonly aiAuthorized: boolean | null;
  readonly aiProfile: FoxKifuAiPublicProfile | null;
  readonly aiAnalysis: FoxKifuAiAnalysis | null;
  readonly observationAiPosition: ObservationAnalysisPosition | null;
  readonly aiVariation: FoxKifuAiVariationData | null;
  readonly aiVariationPending: boolean;
  readonly aiPending: boolean;
  readonly aiError: string | null;
  readonly pending: boolean;
  readonly error: string | null;
}

type Listener = () => void;

function errorMessage(error: unknown): string {
  return localizeGameError(
    error instanceof WebApiError ? error.code : undefined,
    error instanceof Error ? error.message : undefined,
    '历史棋谱加载失败，请稍后重试。',
  );
}

export class GameReplayStore {
  private stateValue: GameReplayState = this.initialState();
  private readonly listeners = new Set<Listener>();
  private loadGeneration = 0;
  private aiGeneration = 0;
  private aiVariationGeneration = 0;
  private researchGeneration = 0;
  private aiStatusGeneration = 0;
  private activeAnalysisId: string | null = null;

  constructor(private readonly identity: IdentityStore) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): GameReplayState => this.stateValue;

  async load(gameId: string): Promise<void> {
    this.cancelAi();
    this.clearResearch();
    const generation = ++this.loadGeneration;
    const previousReview = this.stateValue.review;
    this.update({
      ...this.stateValue,
      review: previousReview,
      research: null,
      pending: true,
      error: null,
    });
    try {
      const response = await this.identity.authorized(
        `/games/${encodeURIComponent(gameId)}/review`,
        gameReviewResponseSchema,
      );
      if (generation !== this.loadGeneration) return;
      this.update({
        ...this.stateValue,
        review: response.data,
        reviewStateVersion: response.stateVersion,
        pending: false,
        error: null,
      });
    } catch (error: unknown) {
      if (generation !== this.loadGeneration) return;
      this.update({
        ...this.stateValue,
        review: previousReview,
        pending: false,
        error: errorMessage(error),
      });
      throw error;
    }
  }

  async refreshAiStatus(): Promise<void> {
    const generation = ++this.aiStatusGeneration;
    try {
      const response = await this.identity.authorized(
        '/fox-kifu/ai/status',
        foxKifuAiStatusResponseSchema,
      );
      if (generation !== this.aiStatusGeneration) return;
      this.update({
        ...this.stateValue,
        aiServiceEnabled: response.data.enabled,
        aiAvailable: response.data.enabled && response.data.available,
        aiConfigured: response.data.configured,
        aiAuthorized: response.data.authorized,
        ...selectionFromStatus(this.stateValue, response.data),
        aiError: null,
      });
    } catch {
      if (generation !== this.aiStatusGeneration) return;
      this.update({
        ...this.stateValue,
        aiAvailable: false,
        aiConfigured: null,
        aiAuthorized: null,
        aiProfile: null,
      });
    }
  }

  selectAiAccount(configurationId: string): void {
    const account = this.stateValue.aiSelection?.accounts?.find(
      (item) => item.configurationId === configurationId,
    );
    if (account === undefined) return;
    this.cancelAi();
    this.update({
      ...this.stateValue,
      aiSelectedConfigurationId: configurationId,
      aiProfile: account.profile,
    });
  }

  async saveAiDefault(): Promise<void> {
    const generation = this.aiStatusGeneration;
    const configurationId = this.stateValue.aiSelectedConfigurationId;
    if (!configurationId) return;
    const response = await this.identity.authorized(
      '/platform/fox-kifu-ai/my-default',
      foxKifuAiDefaultResponseSchema,
      {
        method: 'PUT',
        requestId: nextRequestId('ai-default'),
        body: { configurationId, version: this.stateValue.aiSelection?.defaultVersion ?? 0 },
      },
    );
    if (generation !== this.aiStatusGeneration) return;
    const selection = this.stateValue.aiSelection;
    if (selection)
      this.update({
        ...this.stateValue,
        aiSelection: {
          ...selection,
          defaultConfigurationId: response.data.configurationId,
          defaultVersion: response.data.version,
          selectionSource: 'DEFAULT',
        },
      });
  }

  async analyze(
    gameId: string,
    baseMoveNumber: number,
    moves: readonly BoardPoint[],
  ): Promise<void> {
    return this.analyzePosition(gameId, baseMoveNumber, moves);
  }

  async loadObservationTimeline(gameId: string, stateVersion: number, retry = false) {
    const configurationId = this.stateValue.aiSelectedConfigurationId;
    return this.identity.authorized(
      `/fox-kifu/observations/${encodeURIComponent(gameId)}/ai-timeline`,
      foxKifuObservationTimelineResponseSchema,
      {
        method: 'POST',
        body: { stateVersion, retry, ...(configurationId ? { configurationId } : {}) },
      },
    );
  }

  /** Read durable results without creating a provider task or changing AI connection state. */
  async readObservationTimeline(gameId: string, stateVersion: number) {
    const query = new URLSearchParams({ stateVersion: String(stateVersion) });
    const configurationId = this.stateValue.aiSelectedConfigurationId;
    if (configurationId) query.set('configurationId', configurationId);
    return this.identity.authorized(
      `/fox-kifu/observations/${encodeURIComponent(gameId)}/ai-timeline?${query}`,
      foxKifuObservationTimelineResponseSchema,
      { method: 'GET' },
    );
  }

  async analyzeObservation(position: ObservationAnalysisPosition): Promise<void> {
    return this.analyzePosition(position.gameId, position.baseMoveNumber, position.moves, position);
  }

  private async analyzePosition(
    gameId: string,
    baseMoveNumber: number,
    moves: readonly BoardPoint[],
    observation?: ObservationAnalysisPosition,
  ): Promise<void> {
    const configurationId = this.stateValue.aiSelectedConfigurationId;
    this.cancelActiveAnalysis();
    const generation = ++this.aiGeneration;
    this.aiVariationGeneration += 1;
    const continuous = this.stateValue.aiProfile?.maxVisits === 0;
    let visibleAnalysis: FoxKifuAiAnalysis | null = null;
    let retryAttempt = 0;
    this.update({
      ...this.stateValue,
      aiAnalysis: null,
      observationAiPosition: observation ?? null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: true,
      aiError: null,
    });
    try {
      while (generation === this.aiGeneration) {
        const analysisId = nextRequestId('game-review-ai');
        this.activeAnalysisId = analysisId;
        let runningWithoutResultSince: number | null = null;
        const waitingSince = Date.now();
        try {
          const options = {
            method: 'POST' as const,
            requestId: analysisId,
            body: { baseMoveNumber, moves, ...(configurationId ? { configurationId } : {}) },
          };
          const initial =
            observation === undefined
              ? await this.identity.authorized(
                  `/fox-kifu/games/${encodeURIComponent(gameId)}/ai-analyses`,
                  foxKifuAiAnalysisResponseSchema,
                  options,
                )
              : await this.identity.authorized(
                  `/fox-kifu/observations/${encodeURIComponent(gameId)}/ai-analyses`,
                  foxKifuObservationAiAnalysisResponseSchema,
                  { ...options, body: { ...options.body, stateVersion: observation.stateVersion } },
                );
          if (generation !== this.aiGeneration) {
            this.cancelAnalysis(analysisId);
            return;
          }
          if (
            observation !== undefined &&
            (!('gameId' in initial) ||
              initial.gameId !== gameId ||
              !('stateVersion' in initial) ||
              !('baseMoveNumber' in initial) ||
              initial.stateVersion !== observation.stateVersion ||
              initial.baseMoveNumber !== baseMoveNumber)
          )
            throw new WebApiError('INVALID_SERVER_RESPONSE', '分析局面与请求不一致。', 502);
          let response = initial;
          while (generation === this.aiGeneration) {
            const analysis = response.data.analysis;
            if (analysis.recordId !== gameId || analysis.analysisId !== analysisId)
              throw new WebApiError('INVALID_SERVER_RESPONSE', '分析结果与请求不一致。', 502);
            if (analysis.status === 'SUCCEEDED') {
              this.activeAnalysisId = null;
              visibleAnalysis = analysis;
              retryAttempt = 0;
              this.update({
                ...this.stateValue,
                aiAnalysis: analysis,
                aiPending: false,
                aiError: null,
              });
              if (!continuous) return;
              await waitForAi(AI_ANALYSIS_SLICE_RESTART_MS);
              break;
            }
            if (analysis.status === 'FAILED') {
              throw new WebApiError(
                analysis.errorCode ?? 'PROCESSING_FAILED',
                '智子云未能完成当前局面分析。',
                503,
              );
            }
            if (analysis.status === 'CANCELLED') {
              throw new WebApiError('PROVIDER_UNAVAILABLE', '智子云分析意外中断。', 503);
            }
            // A RUNNING -> QUEUED recovery must not restart the no-result deadline.
            if (
              analysis.result === null &&
              Date.now() - waitingSince >= AI_ANALYSIS_NO_RESULT_STALE_MS
            ) {
              throw new WebApiError('PROVIDER_TIMEOUT', 'AI 分析等待超时，请稍后重试。', 503);
            }
            if (analysis.result !== null) {
              visibleAnalysis = analysis;
              retryAttempt = 0;
              runningWithoutResultSince = null;
            } else if (analysis.status === 'RUNNING') {
              runningWithoutResultSince ??= Date.now();
              if (Date.now() - runningWithoutResultSince >= AI_ANALYSIS_RUNNING_STALE_MS) {
                throw new WebApiError('PROVIDER_TIMEOUT', '智子云未在时限内返回推荐点。', 503);
              }
            } else {
              runningWithoutResultSince = null;
            }
            const displayed = analysis.result === null ? visibleAnalysis : analysis;
            this.update({
              ...this.stateValue,
              aiAnalysis: displayed,
              aiPending: displayed === null,
              aiError: null,
            });
            await waitForAi(AI_ANALYSIS_POLL_INTERVAL_MS);
            if (generation !== this.aiGeneration) return;
            response = await this.identity.authorized(
              `/fox-kifu/ai-analyses/${encodeURIComponent(analysisId)}`,
              foxKifuAiAnalysisResponseSchema,
            );
          }
        } catch (error: unknown) {
          if (generation !== this.aiGeneration) return;
          this.cancelActiveAnalysis();
          retryAttempt += 1;
          if (
            !isRetryableAiError(error) ||
            ((!continuous || visibleAnalysis === null) &&
              retryAttempt > AI_ANALYSIS_MAX_BOUNDED_RETRIES)
          ) {
            throw error;
          }
          this.update({
            ...this.stateValue,
            aiAnalysis: visibleAnalysis,
            aiPending: visibleAnalysis === null,
            aiError: continuous && retryAttempt >= 3 ? '算力连接波动，正在自动重新连接…' : null,
          });
          await waitForAi(aiRetryDelay(retryAttempt));
        }
      }
    } catch (error: unknown) {
      if (generation !== this.aiGeneration) return;
      this.activeAnalysisId = null;
      this.update({
        ...this.stateValue,
        aiAnalysis: null,
        aiPending: false,
        aiError:
          observation !== undefined &&
          error instanceof WebApiError &&
          error.code === 'STATE_VERSION_CONFLICT'
            ? '对局已更新，请点击重试，使用最新同步状态分析当前局面。'
            : error instanceof WebApiError && error.code === 'PROVIDER_TIMEOUT'
              ? 'AI 分析长时间未返回结果，请稍后重试。'
              : error instanceof WebApiError && error.code === 'RESULT_INVALID'
                ? 'AI 返回结果未通过围棋规则校验。'
                : observation !== undefined
                  ? userErrorMessage(error, '实时局面分析失败，请稍后重试。')
                  : '智子云算力暂时不可用，请稍后再试。',
      });
      throw error;
    }
  }

  async research(
    gameId: string,
    baseMoveNumber: number,
    moves: readonly BoardPoint[],
  ): Promise<boolean> {
    return this.researchPosition(gameId, baseMoveNumber, moves);
  }

  async researchObservation(position: ObservationAnalysisPosition): Promise<boolean> {
    return this.researchPosition(
      position.gameId,
      position.baseMoveNumber,
      position.moves,
      position,
    );
  }

  private async researchPosition(
    gameId: string,
    baseMoveNumber: number,
    moves: readonly BoardPoint[],
    observation?: ObservationAnalysisPosition,
  ): Promise<boolean> {
    const generation = ++this.researchGeneration;
    this.update({ ...this.stateValue, researchPending: true, error: null });
    try {
      const response =
        observation === undefined
          ? await this.identity.authorized(
              `/fox-kifu/games/${encodeURIComponent(gameId)}/research`,
              foxKifuResearchResponseSchema,
              {
                method: 'POST',
                requestId: nextRequestId('game-research'),
                body: { baseMoveNumber, moves },
              },
            )
          : await this.identity.authorized(
              `/fox-kifu/observations/${encodeURIComponent(gameId)}/research`,
              foxKifuObservationResearchResponseSchema,
              {
                method: 'POST',
                requestId: nextRequestId('observation-research'),
                body: { baseMoveNumber, moves, stateVersion: observation.stateVersion },
              },
            );
      if (generation !== this.researchGeneration) return false;
      if (
        response.data.recordId !== gameId ||
        response.data.baseMoveNumber !== baseMoveNumber ||
        response.data.variationMoveCount !== moves.length ||
        (observation !== undefined &&
          (!('stateVersion' in response) || response.stateVersion !== observation.stateVersion))
      )
        throw new WebApiError('INVALID_SERVER_RESPONSE', '研究局面与请求不一致。', 502);
      this.update({ ...this.stateValue, research: response.data, researchPending: false });
      return true;
    } catch (error: unknown) {
      if (generation !== this.researchGeneration) return false;
      this.update({
        ...this.stateValue,
        researchPending: false,
        error:
          observation !== undefined &&
          error instanceof WebApiError &&
          error.code === 'STATE_VERSION_CONFLICT'
            ? '对局已更新，请点击重试，使用最新同步状态重新校验研究变化。'
            : userErrorMessage(error, '试下落子失败，请重新选择交叉点。'),
      });
      throw error;
    }
  }

  clearResearch(): void {
    this.researchGeneration += 1;
    this.update({ ...this.stateValue, research: null, researchPending: false, error: null });
  }

  cancelAi(): void {
    this.cancelActiveAnalysis();
    this.aiGeneration += 1;
    this.aiVariationGeneration += 1;
    this.update({
      ...this.stateValue,
      aiAnalysis: null,
      observationAiPosition: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: null,
    });
  }

  async loadAiVariation(analysisId: string, candidateRank: number): Promise<void> {
    const generation = ++this.aiVariationGeneration;
    this.update({ ...this.stateValue, aiVariation: null, aiVariationPending: true, aiError: null });
    try {
      const response = await this.identity.authorized(
        `/fox-kifu/ai-analyses/${encodeURIComponent(analysisId)}/variations/${candidateRank}`,
        foxKifuAiVariationResponseSchema,
      );
      if (
        generation !== this.aiVariationGeneration ||
        this.stateValue.aiAnalysis?.analysisId !== analysisId
      )
        return;
      this.update({
        ...this.stateValue,
        aiVariation: response.data,
        aiVariationPending: false,
        aiError: null,
      });
    } catch (error: unknown) {
      if (
        generation !== this.aiVariationGeneration ||
        this.stateValue.aiAnalysis?.analysisId !== analysisId
      )
        return;
      this.update({
        ...this.stateValue,
        aiVariation: null,
        aiVariationPending: false,
        aiError: userErrorMessage(error, 'AI 变化图载入失败，请稍后重试。'),
      });
      throw error;
    }
  }

  clearAiVariation(): void {
    this.aiVariationGeneration += 1;
    this.update({ ...this.stateValue, aiVariation: null, aiVariationPending: false });
  }

  clearAiError(): void {
    this.update({ ...this.stateValue, aiError: null });
  }

  clear(): void {
    this.aiStatusGeneration++;
    this.loadGeneration += 1;
    this.researchGeneration += 1;
    this.cancelActiveAnalysis();
    this.aiGeneration += 1;
    this.aiVariationGeneration += 1;
    this.update(this.initialState());
  }

  private cancelActiveAnalysis(): void {
    const analysisId = this.activeAnalysisId;
    this.activeAnalysisId = null;
    if (analysisId === null) return;
    this.cancelAnalysis(analysisId);
  }

  private cancelAnalysis(analysisId: string): void {
    void this.identity
      .authorized(
        `/fox-kifu/ai-analyses/${encodeURIComponent(analysisId)}/cancel`,
        foxKifuAiAnalysisResponseSchema,
        { method: 'POST', requestId: nextRequestId('game-review-ai-cancel') },
      )
      .catch(() => undefined);
  }

  private initialState(): GameReplayState {
    return {
      review: null,
      reviewStateVersion: null,
      research: null,
      researchPending: false,
      aiServiceEnabled: null,
      aiAvailable: null,
      aiConfigured: null,
      aiAuthorized: null,
      aiProfile: null,
      aiAnalysis: null,
      observationAiPosition: null,
      aiVariation: null,
      aiVariationPending: false,
      aiPending: false,
      aiError: null,
      pending: false,
      error: null,
    };
  }

  private update(state: GameReplayState): void {
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
  }
}
